import React from "react";
import { useParams } from "react-router-dom";
import List from "components/list";
import CreateItemButton from "components/buttons/create";
import HeaderPortal from "components/portal/header-portal";
import { PermissionControlledInput } from "permissions/components/input";
import { DateCell } from "@/components/date/cell";
import { LinkCell } from "@/components/link/cell";
import { CommonCell } from "components/cell/common";
import { ROUTE } from "@/lib/utils";
import { assetUrl } from "utils";
import { FileVideo, FileImage } from "lucide-react";
import { useProject } from "../context";
import {
  COLLECTION,
  FILES_FIELD,
  FILES_JUNCTION_KEY,
  SKILLS_FIELD,
  SKILLS_JUNCTION_KEY,
} from "./constants";

export default function ProjectDeliverables() {
  const { project_slug } = useParams();
  const { id } = useProject();
  if (!id) return null;

  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
    },
    {
      id: "type",
      header: "Type",
      accessorKey: "type",
      cell: (props) => (
        <CommonCell {...props} collection={COLLECTION} field="type" />
      ),
    },
    {
      id: "url",
      header: "URL",
      accessorKey: "url",
      cell: LinkCell,
    },
    {
      id: "skills",
      header: "Skills",
      accessorKey: SKILLS_FIELD,
      cell: ({ getValue }) => {
        const names = (getValue() ?? [])
          .map((row) => row?.[SKILLS_JUNCTION_KEY]?.name)
          .filter(Boolean);
        return names.length ? (
          <div className="max-w-xs truncate">{names.join(", ")}</div>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      id: "files",
      header: "Files",
      accessorKey: FILES_FIELD,
      cell: ({ getValue }) => <DeliverableFilesCell rows={getValue()} />,
    },
    {
      id: "date_created",
      header: "Created on",
      accessorKey: "date_created",
      cell: DateCell,
    },
  ];

  const fields = [
    "id",
    "name",
    "type",
    "url",
    `${SKILLS_FIELD}.${SKILLS_JUNCTION_KEY}.name`,
    `${FILES_FIELD}.id`,
    `${FILES_FIELD}.${FILES_JUNCTION_KEY}.id`,
    `${FILES_FIELD}.${FILES_JUNCTION_KEY}.type`,
    `${FILES_FIELD}.${FILES_JUNCTION_KEY}.filename_download`,
    "date_created",
  ];

  return (
    <div className="flex-1 w-full">
      <HeaderPortal>
        <PermissionControlledInput
          rule="create:*"
          collection={COLLECTION}
          showWhenDisabled={false}
        >
          <CreateItemButton href>Deliverable</CreateItemButton>
        </PermissionControlledInput>
      </HeaderPortal>
      <List
        title="Deliverables"
        endPoint={COLLECTION}
        storageKey={`deliverables-${id}`}
        filters={{ project: { _eq: id } }}
        columns={columns}
        meta={{ fields }}
        href={(item) => `${ROUTE.PROJECT_DELIVERABLES(project_slug)}/${item.id}`}
      />
    </div>
  );
}

const MAX_THUMBS = 4;

// Small previews of the deliverable's uploaded files; each opens the file in a new tab
function DeliverableFilesCell({ rows }) {
  const files = (rows ?? [])
    .map((row) => row?.[FILES_JUNCTION_KEY])
    .filter((file) => file?.id);
  if (!files.length) return <span>-</span>;
  const extra = files.length - MAX_THUMBS;
  return (
    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      {files.slice(0, MAX_THUMBS).map((file) => {
        const url = assetUrl(file.id);
        const type = file.type || "";
        return (
          <a
            key={file.id}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={file.filename_download}
            className="size-9 shrink-0 overflow-hidden rounded border bg-gray-100 flex items-center justify-center"
          >
            {type.startsWith("image/") ? (
              <img
                src={`${url}?width=72&height=72&fit=cover`}
                alt={file.filename_download}
                className="size-full object-cover"
              />
            ) : type.startsWith("video/") ? (
              <FileVideo className="size-4 text-gray-500" />
            ) : (
              <FileImage className="size-4 text-gray-500" />
            )}
          </a>
        );
      })}
      {extra > 0 && <span className="text-xs text-gray-500">+{extra}</span>}
    </div>
  );
}
